// reports/reportImages.ts
//
// Resolves the imagery a report sheet needs into data URLs.
//
// The report document is rendered in an isolated iframe (and, on mobile, in a
// WebView), neither of which carries the app's auth token or cookie. A `src`
// pointing at an ownership-checked image endpoint would therefore 401 and print
// an empty frame — silently, because a missing image is not an error to a layout
// engine. Inlining sidesteps the whole problem: by the time the document exists,
// every pixel it references is already inside it.
//
// Data URLs rather than `blob:` URLs: a blob URL is revoked when its owner goes
// away, and print spooling outlives the JavaScript that started it. Inlining
// costs roughly a third more bytes in memory for the duration of the export and
// removes an entire class of blank-image bug.

import { fetchImageBlob } from '../api/imageBlobCache';
import type { ReportImages } from './viewModel';

/** Read a blob as a `data:` URL. */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { resolve(typeof reader.result === 'string' ? reader.result : ''); };
    reader.onerror = () => { reject(reader.error ?? new Error('Could not read image data.')); };
    reader.readAsDataURL(blob);
  });
}

/** Fetch one authenticated image path and inline it. Undefined if unavailable. */
async function inline(path?: string): Promise<string | undefined> {
  if (!path) return undefined;
  try {
    return await blobToDataUrl(await fetchImageBlob(path));
  } catch {
    // A missing portrait or atlas degrades to the template's placeholder zones,
    // which is a legitimate sheet — far better than failing the export outright.
    return undefined;
  }
}

/**
 * Resolve a job's portrait and analysis atlas for a report render.
 *
 * Both are fetched together because a sheet uses both and neither blocks the
 * other, and both are optional: a report still prints without them.
 */
export async function resolveReportImages(paths: {
  portrait?: string;
  atlas?: string;
}): Promise<ReportImages> {
  const [portrait, atlas] = await Promise.all([inline(paths.portrait), inline(paths.atlas)]);
  return { portrait, atlas };
}
